<template>
  <v-container fluid class="px-12 pt-8 pb-2">
    <breadcrumbs></breadcrumbs>
    <h1>{{ id ? "Edit" : "Create" }} Question</h1>
    <v-card
      v-if="parentAnswer && parentQuestion"
      class="elevation-0 mt-5"
      outlined
    >
      <v-card-text class="pb-0">
        <div class="subheading">
          <v-icon small class="mr-2">fas fa-exclamation-triangle</v-icon
          >Question Dependency -- This question has the following parent
          question and answer combination:
        </div>
        <v-row>
          <v-col cols="6">
            <span class="och-label">Question:</span>
            <v-alert
              dense
              small
              border="left"
              color="accent"
              colored-border
              class="mt-2 pl-6"
            >
              <v-btn
                @click="
                  $router.push({
                    name: 'ochinUserSecurityQuestionForm',
                    params: { questionId: parentQuestion.id },
                  })
                "
                text
                >{{ parentQuestion.text }}</v-btn
              >
            </v-alert>
          </v-col>
          <v-col cols="6">
            <span class="och-label">Answer:</span>
            <v-alert
              dense
              small
              border="left"
              color="secondary"
              colored-border
              class="mt-2 pl-6"
            >
              <v-btn
                @click="
                  $router.push({
                    name: 'ochinUserSecurityAnswerForm',
                    params: {
                      answerId: parentAnswer.id,
                      questionId: parentQuestion.id,
                    },
                  })
                "
                text
              >
                {{ parentAnswer.text }}</v-btn
              >
            </v-alert>
          </v-col>
        </v-row>
        <div class="text-right mb-5">
          <v-btn small outlined color="primary" @click="removeDependency"
            >Remove Dependency</v-btn
          >
        </div>
      </v-card-text>
    </v-card>
    <v-alert class="mt-3" v-if="record.isDeprecated" type="warning">
      This question and its answers have been deprecated and cannot be edited.
    </v-alert>
    <v-row justify="center" v-if="showForm">
      <v-col cols="12">
        <v-form @submit.prevent="onSubmit" v-model="valid" class="mt-5">
          <label class="och-label">Question Text</label>
          <v-text-field v-model="text" outlined counter="255"></v-text-field>
          <label class="och-label">Question Help Text</label>
          <v-textarea
            clearable
            counter="1000"
            outlined
            v-model="helpText"
          ></v-textarea>
        </v-form>

        <div class="text-right mt-5">
          <v-btn v-if="id" @click="resetForm" outlined class="mr-2"
            >Cancel</v-btn
          >
          <v-btn
            :disabled="!valid || !!record.isDeprecated"
            @click="save"
            color="primary"
            class="elevation-0"
            >Save</v-btn
          >
        </div>
      </v-col>
    </v-row>
    <div v-else>
      <v-divider class="mb-3 mt-5"></v-divider>
      <v-row>
        <v-col cols="9">
          <label class="och-label caption">Question text</label>
          <div class="headline">{{ record.text }}</div>
        </v-col>
        <v-col cols="3" class="text-right mt-2">
          <v-btn
            color="primary"
            :disabled="!!record.isDeprecated"
            outlined
            @click="showEditForm"
            >Edit</v-btn
          >
        </v-col>
        <v-col cols="9">
          <div>{{ record.helpText }}</div>
        </v-col>
      </v-row>
      <v-divider class="mb-5 mt-3"></v-divider>

      <v-row class="mt-10">
        <v-col cols="9">
          <div class="title">Answers</div>
        </v-col>
        <v-col cols="3" class="text-right">
          <v-btn
            :disabled="!!record.isDeprecated"
            outlined
            color="primary"
            @click="$router.push({ name: 'ochinUserSecurityAnswerCreate' })"
            >Add</v-btn
          >
        </v-col>
      </v-row>
      <answer-table
        :questionIsDeprecated="!!record.isDeprecated"
      ></answer-table>
    </div>
  </v-container>
</template>
<script>
import { cloneDeep } from "lodash";
import { mapActions, mapGetters } from "vuex";
import AnswerTable from "./answer/Table";
import Breadcrumbs from "../Breadcrumbs";

export default {
  components: {
    AnswerTable,
    Breadcrumbs,
  },
  computed: {
    id() {
      return parseInt(this.$route.params.questionId, 10);
    },
    parentAnswerId() {
      const queryParam = this.$route.query.parentAnswerId;
      return queryParam ? parseInt(queryParam, 10) : null;
    },
    ...mapGetters({
      getQuestionById: "secQuestion/getById",
      getPrimaryRoleById: "secRolePrimary/getById",
    }),
  },
  async created() {
    await this.init();
  },
  data() {
    return {
      helpText: null,
      parentAnswer: null,
      parentQuestion: null,
      record: {},
      showForm: false,
      text: null,
      valid: false,
    };
  },
  methods: {
    async init(val) {
      const id = val || this.id;
      this.parentAnswer = null;
      this.parentQuestion = null;

      if (!id) {
        this.showForm = true;
      } else {
        this.record = await this.getQuestion(id);
      }

      const parentAnswerId = this.parentAnswerId || this.record.parentAnswerId;

      if (parentAnswerId) {
        this.parentAnswer = await this.getAnswer(parentAnswerId);
        this.parentQuestion = await this.getQuestion(
          this.parentAnswer.secQuestionId
        );
      }
    },
    showEditForm() {
      this.helpText = this.record.helpText;
      this.text = this.record.text;
      this.showForm = true;
    },
    removeDependency() {
      this.parentAnswer = null;
      this.parentQuestion = null;
    },
    resetForm() {
      this.showForm = false;
    },
    async save() {
      if (!this.helpText || this.helpText.trim().length === 0) {
        this.helpText = null;
      }
      const body = {
        helpText: this.helpText,
        text: this.text,
      };
      if (this.parentAnswer) {
        body.parentAnswerId = this.parentAnswer.id;
      } else {
        body.parentAnswerId = null;
      }
      if (this.id) {
        await this.updateQuestion([this.id, body]);
        await this.getQuestion(this.id);
        this.record = cloneDeep(this.$store.state.secQuestion.record);
        this.showForm = false;
        return;
      } else {
        const result = await this.createQuestion(body);
        this.record = cloneDeep(result);
        this.$router.push({
          name: "ochinUserSecurityQuestionForm",
          params: { questionId: result.id },
        });
        return;
      }
    },
    ...mapActions({
      createQuestion: "secQuestion/create",
      getAnswer: "secAnswer/get",
      getQuestion: "secQuestion/get",
      updateQuestion: "secQuestion/update",
    }),
  },
  watch: {
    async id(val) {
      await this.init(val);
    },
  },
};
</script>
